/**
 * "Order for a student" — a school admin orders meals for one of its rostered
 * students and the school pays (SCHOOL_FOR_STUDENT). The order sits on the
 * student's REAL record: the cafeteria serves it, the family sees it, and a
 * second meal for the same sitting is refused. It replaces the stand-in school
 * household that held copies of students.
 *
 * One order per student sitting, like a parent's checkout: a meal, à la carte
 * items, or both. A student on a free or reduced plan keeps it: the plan
 * covers its share and the school pays the rest.
 */
import { Button } from '@ordo/ui/button'
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from '@ordo/ui/dialog'
import { Input } from '@ordo/ui/input'
import { Label } from '@ordo/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ordo/ui/select'
import { Skeleton } from '@ordo/ui/skeleton'
import { type ReactElement, useMemo, useState } from 'react'

import { useSchoolBillingAccountQuery } from '@/api/billing-school-account'
import { ApiError } from '@/api/http'
import { useLocationActiveMenusQuery } from '@/api/orders'
import type { AdminSchool } from '@/api/school-orders'
import {
  type PlaceSchoolStudentOrderVars,
  type SchoolStudentOrderWire,
  useCancelSchoolStudentOrderMutation,
  usePlaceSchoolStudentOrderMutation,
  useSchoolStudentOrdersQuery,
} from '@/api/school-student-orders'
import type { StudentSearchResultWire } from '@/api/special-diet-accommodations'
import { CampusTopUpBlock } from '@/components/billing/campus-top-up-block'
import { menuItemName, QtyInput } from '@/components/orders/school/day-order-table'
import { StudentRosterPicker } from '@/components/settings/student-roster-picker'
import { type Mealtime, MEALTIME_LABELS } from '@/lib/mealtime'
import { type ActiveMenuItem, orderableMenusFor } from '@/lib/school-orders/active-menus'
import { addDaysKey, monthDayLabel, todayKey } from '@/lib/school-orders/calendar'
import { firstOpenOrderDay, orderClosedReason, schoolDeadlinePolicy } from '@/lib/school-orders/deadline'
import { schoolFundsGate } from '@/lib/school-orders/funds'
import { formatCents } from '@/lib/school-orders/money'

/** How far ahead the dialog lists the school's student orders. */
const ORDER_LIST_DAYS = 60

/** The most of one à la carte item per order — the server's cap (routes/school-student-orders.ts). */
const MAX_ITEM_QUANTITY = 10

/**
 * A meal the school can order for a student: a priced meal bundle, never a
 * field-trip (bagged) item — the server refuses those on this path too.
 */
export function isSchoolStudentMeal(item: ActiveMenuItem): boolean {
  return item.itemKind === 'BUNDLE' && item.isPriced && item.customerPriceCents !== null && item.isFieldTrip !== true
}

/**
 * An à la carte item the school can order for a student, with a meal or on
 * its own, as a parent can: a priced component. Never a field-trip item, and
 * never the menu's system milk row — that milk comes with the meal, and a
 * parent's menu never lists it.
 */
export function isSchoolStudentAlaCarte(item: ActiveMenuItem): boolean {
  return (
    item.itemKind === 'COMPONENT' &&
    item.isPriced &&
    item.customerPriceCents !== null &&
    item.isFieldTrip !== true &&
    item.lineupMilkOptionCode == null
  )
}

export interface AlaCartePick {
  item: ActiveMenuItem
  quantity: number
}

/**
 * The à la carte items given a quantity, in menu order. Only items the menu
 * still offers count: a refetch after a price change can drop one.
 */
export function pickedAlaCarte(
  offered: readonly ActiveMenuItem[],
  quantities: Readonly<Record<string, number>>,
): AlaCartePick[] {
  return offered.flatMap((item) => {
    const quantity = quantities[item.id] ?? 0
    return quantity > 0 ? [{ item, quantity }] : []
  })
}

/** The order's price before the student's meal plan: the meal plus each à la carte item × quantity. */
export function schoolStudentOrderTotalCents(meal: ActiveMenuItem | null, alaCarte: readonly AlaCartePick[]): number {
  return alaCarte.reduce(
    (sum, { item, quantity }) => sum + (item.customerPriceCents ?? 0) * quantity,
    meal?.customerPriceCents ?? 0,
  )
}

/**
 * The order's items as the POST takes them, each at the price shown. A
 * meal-only order sends no `alaCarte` key, so its body is the one the server
 * took before à la carte existed.
 */
export function schoolStudentOrderItems(
  meal: ActiveMenuItem | null,
  alaCarte: readonly AlaCartePick[],
): Pick<PlaceSchoolStudentOrderVars, 'menuItemId' | 'expectedUnitPriceCents' | 'alaCarte'> {
  return {
    ...(meal ? { menuItemId: meal.id, expectedUnitPriceCents: meal.customerPriceCents ?? 0 } : {}),
    ...(alaCarte.length > 0
      ? {
          alaCarte: alaCarte.map(({ item, quantity }) => ({
            menuItemId: item.id,
            quantity,
            expectedUnitPriceCents: item.customerPriceCents ?? 0,
          })),
        }
      : {}),
  }
}

/** "Potato Wedges", or "Potato Wedges ×2". */
export function orderLineLabel(line: { name: string; quantity: number }): string {
  return line.quantity > 1 ? `${line.name} ×${line.quantity}` : line.name
}

/** The message an order refusal shows, by the server's error code. */
export function schoolStudentOrderErrorMessage(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return 'Unable to place the order. Try again.'
  }
  if (error.code === 'insufficient_funds') {
    return 'The school balance can’t cover this order. Add funds, then order again.'
  }
  if (error.code === 'price_changed') {
    return 'A price on this order just changed. Check the new prices and order again.'
  }
  // Every other refusal (already ordered for the sitting, ordering closed,
  // not on the student's grade menu) arrives with a message written for staff.
  return error.message
}

export function StudentOrderDialog({
  school,
  open,
  onOpenChange,
}: {
  school: AdminSchool
  open: boolean
  onOpenChange: (open: boolean) => void
}): ReactElement {
  const today = todayKey()
  const policy = schoolDeadlinePolicy(school)
  // The school's ordering deadline: days before this one have closed, and the
  // server refuses them (assertOrderingOpen) — so the picker never offers them.
  const firstOpenDay = open ? firstOpenOrderDay(policy, today) : today
  const [student, setStudent] = useState<StudentSearchResultWire | null>(null)
  const [date, setDate] = useState('')
  const [mealtime, setMealtime] = useState<Mealtime | ''>('')
  const [menuItemId, setMenuItemId] = useState('')
  const [alaCarteQty, setAlaCarteQty] = useState<Record<string, number>>({})
  const [attemptKey, setAttemptKey] = useState(() => crypto.randomUUID())
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null)

  const dateClosed = date !== '' && orderClosedReason(date, policy, today) !== null
  const menus = useLocationActiveMenusQuery({
    locationId: open && date && !dateClosed ? school.id : '',
    activeOn: date && !dateClosed ? date : null,
  })
  const orderable = useMemo(() => orderableMenusFor(menus.data), [menus.data])
  const offered = mealtime ? (orderable.itemsByMealtime.get(mealtime) ?? []) : []
  const meals = offered.filter(isSchoolStudentMeal)
  const alaCarte = offered.filter(isSchoolStudentAlaCarte)
  const chosen = meals.find((meal) => meal.id === menuItemId) ?? null
  const alaCartePicks = pickedAlaCarte(alaCarte, alaCarteQty)
  const hasPicks = chosen !== null || alaCartePicks.length > 0
  const totalCents = schoolStudentOrderTotalCents(chosen, alaCartePicks)

  const orders = useSchoolStudentOrdersQuery({
    locationId: school.id,
    from: today,
    to: addDaysKey(today, ORDER_LIST_DAYS),
    enabled: open,
  })
  const shownOrders = (orders.data?.orders ?? []).filter((order) => !student || order.student.id === student.userId)
  const alreadyOrdered =
    student && date && mealtime
      ? shownOrders.find((order) => order.serviceDate === date && order.mealtime === mealtime)
      : undefined

  const account = useSchoolBillingAccountQuery(open ? school.id : null)
  const gate = account.data && hasPicks ? schoolFundsGate(account.data, totalCents) : { gated: false as const }

  const place = usePlaceSchoolStudentOrderMutation()
  const cancel = useCancelSchoolStudentOrderMutation()

  function resetChoice(): void {
    setMenuItemId('')
    setAlaCarteQty({})
    setAttemptKey(crypto.randomUUID())
  }

  const canSubmit =
    student !== null &&
    !dateClosed &&
    hasPicks &&
    !alreadyOrdered &&
    !gate.gated &&
    !place.isPending &&
    orders.data !== undefined

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Order for a student · {school.name}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-6">
          <form
            noValidate
            className="space-y-4"
            onSubmit={async (event) => {
              event.preventDefault()
              if (!canSubmit || !student || !mealtime) {
                return
              }
              setMessage(null)
              try {
                await place.mutateAsync({
                  locationId: school.id,
                  studentUserId: student.userId,
                  serviceDate: date,
                  mealtime,
                  ...schoolStudentOrderItems(chosen, alaCartePicks),
                  idempotencyKey: attemptKey,
                })
                const ordered = [
                  ...(chosen ? [menuItemName(chosen)] : []),
                  ...alaCartePicks.map(({ item, quantity }) => orderLineLabel({ name: menuItemName(item), quantity })),
                ]
                setMessage({ tone: 'ok', text: `Ordered ${ordered.join(', ')} for ${student.name}.` })
                resetChoice()
              } catch (error) {
                setMessage({ tone: 'error', text: schoolStudentOrderErrorMessage(error) })
                // Every refusal ends this attempt; the next one is a new checkout.
                setAttemptKey(crypto.randomUUID())
                if (error instanceof ApiError && error.code === 'price_changed') {
                  void menus.refetch()
                }
                if (error instanceof ApiError && error.code === 'insufficient_funds') {
                  void account.refetch()
                }
              }
            }}>
            <p className="text-sm text-muted-foreground">
              The school pays for these meals. A student’s free or reduced meal plan still applies, and their family
              sees the order.
            </p>
            <StudentRosterPicker
              locationId={school.id}
              schoolName={school.name}
              picked={student}
              onPick={(next) => {
                setStudent(next)
                resetChoice()
                setMessage(null)
              }}
              inputId="student-order-student"
              ambiguousHint="Confirm which one with the office before ordering."
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="student-order-date">Service date</Label>
                <Input
                  id="student-order-date"
                  type="date"
                  min={firstOpenDay}
                  value={date}
                  onChange={(event) => {
                    setDate(event.target.value)
                    setMealtime('')
                    resetChoice()
                  }}
                  aria-invalid={dateClosed || undefined}
                  aria-describedby="student-order-date-hint"
                />
                <p
                  id="student-order-date-hint"
                  className={dateClosed ? 'text-xs text-destructive' : 'text-xs text-muted-foreground'}>
                  {dateClosed
                    ? `Ordering has closed for this date. The earliest you can order for is ${monthDayLabel(firstOpenDay)}.`
                    : `Orders open from ${monthDayLabel(firstOpenDay)}.`}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="student-order-mealtime">Mealtime</Label>
                <Select
                  value={mealtime}
                  disabled={!date || dateClosed || orderable.mealtimes.length === 0}
                  onValueChange={(value) => {
                    setMealtime(value as Mealtime)
                    resetChoice()
                  }}>
                  <SelectTrigger id="student-order-mealtime" className="w-full">
                    <SelectValue placeholder="Choose a mealtime" />
                  </SelectTrigger>
                  <SelectContent>
                    {orderable.mealtimes.map((meal) => (
                      <SelectItem key={meal} value={meal}>
                        {MEALTIME_LABELS[meal] ?? meal}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {date && !dateClosed && menus.isPending ? <Skeleton className="h-24 w-full" /> : null}
            {date && menus.data && orderable.mealtimes.length === 0 ? (
              <p className="text-sm text-muted-foreground">No meals are served on this date.</p>
            ) : null}
            {mealtime && menus.data && meals.length === 0 && alaCarte.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nothing can be ordered for this mealtime.</p>
            ) : null}
            {meals.length > 0 ? (
              <fieldset className="space-y-1.5" disabled={place.isPending || Boolean(alreadyOrdered)}>
                <legend className="mb-1 text-sm font-medium">Meal</legend>
                {meals.map((meal) => (
                  <label key={meal.id} className="flex items-center gap-2 rounded-md border p-2 text-sm">
                    <input
                      type="radio"
                      name="student-order-meal"
                      checked={menuItemId === meal.id}
                      onChange={() => setMenuItemId(meal.id)}
                    />
                    <span className="flex-1">{menuItemName(meal)}</span>
                    <span className="tabular-nums text-muted-foreground">
                      {formatCents(meal.customerPriceCents ?? 0, meal.currency ?? 'USD')}
                    </span>
                  </label>
                ))}
                {alaCarte.length > 0 ? (
                  <label className="flex items-center gap-2 rounded-md border p-2 text-sm">
                    <input
                      type="radio"
                      name="student-order-meal"
                      checked={menuItemId === ''}
                      onChange={() => setMenuItemId('')}
                    />
                    <span className="flex-1">No meal, à la carte only</span>
                  </label>
                ) : null}
              </fieldset>
            ) : null}
            {alaCarte.length > 0 ? (
              <fieldset className="space-y-1.5" disabled={place.isPending || Boolean(alreadyOrdered)}>
                <legend className="mb-1 text-sm font-medium">À la carte</legend>
                {alaCarte.map((item) => (
                  <div key={item.id} className="flex items-center gap-2 rounded-md border p-2 text-sm">
                    <span className="flex-1">{menuItemName(item)}</span>
                    <span className="tabular-nums text-muted-foreground">
                      {formatCents(item.customerPriceCents ?? 0, item.currency ?? 'USD')}
                    </span>
                    <QtyInput
                      value={alaCarteQty[item.id] ?? 0}
                      onChange={(quantity) => setAlaCarteQty((prev) => ({ ...prev, [item.id]: quantity }))}
                      max={MAX_ITEM_QUANTITY}
                      ariaLabel={`${menuItemName(item)} quantity`}
                      className="h-8 w-16 text-sm"
                    />
                  </div>
                ))}
              </fieldset>
            ) : null}
            {alreadyOrdered ? (
              <p role="status" className="text-sm font-medium">
                {student?.name} already has {alreadyOrdered.items.map(orderLineLabel).join(', ')} from the school for
                this meal.{alreadyOrdered.canCancel ? ' To change it, cancel it below and order again.' : ''}
              </p>
            ) : null}
            {gate.gated && account.data ? (
              <CampusTopUpBlock
                school={school}
                campusNames={[school.name]}
                account={account.data}
                shortfallCents={gate.shortfallCents}
                onBalanceMaybeChanged={() => void account.refetch()}
              />
            ) : null}
            {message ? (
              <p
                role={message.tone === 'error' ? 'alert' : 'status'}
                className={message.tone === 'error' ? 'text-sm text-destructive' : 'text-sm'}>
                {message.text}
              </p>
            ) : null}
            <div className="flex justify-end border-t pt-4">
              <Button type="submit" disabled={!canSubmit}>
                {place.isPending ? 'Ordering…' : hasPicks ? `Place order · ${formatCents(totalCents)}` : 'Place order'}
              </Button>
            </div>
          </form>

          <section className="space-y-2 border-t pt-4">
            <h3 className="text-sm font-medium">
              {student ? `Upcoming school orders for ${student.name}` : 'Upcoming school orders'}
            </h3>
            {orders.isPending ? (
              <div className="space-y-2">
                <Skeleton className="h-14 w-full" />
                <Skeleton className="h-14 w-full" />
              </div>
            ) : null}
            {orders.error ? <p role="alert">{orders.error.message}</p> : null}
            {orders.data && shownOrders.length === 0 ? (
              <p className="text-sm text-muted-foreground">No upcoming orders.</p>
            ) : null}
            {shownOrders.map((order) => (
              <OrderRow
                key={order.id}
                order={order}
                cancelling={cancel.isPending && cancel.variables?.batchId === order.id}
                onCancel={async () => {
                  setMessage(null)
                  try {
                    await cancel.mutateAsync({ locationId: school.id, batchId: order.id })
                    setMessage({ tone: 'ok', text: `Cancelled ${order.student.name}’s meal on ${order.serviceDate}.` })
                  } catch (error) {
                    setMessage({ tone: 'error', text: schoolStudentOrderErrorMessage(error) })
                  }
                }}
              />
            ))}
          </section>
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}

function OrderRow({
  order,
  cancelling,
  onCancel,
}: {
  order: SchoolStudentOrderWire
  cancelling: boolean
  onCancel: () => void
}): ReactElement {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border p-2 text-sm">
      <div className="min-w-0">
        <p className="font-medium">
          {order.student.name} · {order.serviceDate} · {MEALTIME_LABELS[order.mealtime] ?? order.mealtime}
        </p>
        <p className="truncate text-muted-foreground">
          {order.items.map(orderLineLabel).join(', ')} · School pays {formatCents(order.schoolCents)}
        </p>
      </div>
      {order.canCancel ? (
        <Button type="button" variant="ghost" size="sm" disabled={cancelling} onClick={onCancel}>
          {cancelling ? 'Cancelling…' : 'Cancel'}
        </Button>
      ) : null}
    </div>
  )
}
